import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ChevronLeft, Wine, Sparkles } from 'lucide-react';

// ============================================================
// ALDRIN RESTAURANT - PAGE COCKTAILS SANS ALCOOL
// Design élégant inspiré de la carte fournie
// ============================================================

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Logo officiel
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_resto-stock-manager-3/artifacts/0l7b747s_9F91CBF8-DC1E-49E7-9C24-96206851AEA7.PNG';

// Couleurs
const BRAND_GREEN = '#1a3d2c';
const GOLD = '#c9a962';

// Catégories de cocktails
const COCKTAIL_CATEGORIES = [
    { id: 'signature', name: 'Signature', description: 'Nos créations exclusives' },
    { id: 'fraicheur', name: 'Fraîcheur', description: 'Légers et rafraîchissants' },
    { id: 'classiques', name: 'Classiques', description: 'Les incontournables' }
];

// Données par défaut des cocktails (sera remplacé par les données de l'API)
const DEFAULT_COCKTAILS = [
    // Signature
    {
        id: 'tropical-bissap',
        name: 'Tropical Bissap',
        description: 'Bissap, Ananas, Citron',
        price: 2000,
        category: 'signature',
        image_url: 'https://images.unsplash.com/photo-1536935338788-846bb9981813?w=400'
    },
    {
        id: 'ananas-coco',
        name: 'Ananas Coco',
        description: 'Ananas, Lait de coco',
        price: 2000,
        category: 'signature',
        image_url: 'https://images.unsplash.com/photo-1587223962930-cb7f31384c19?w=400'
    },
    // Fraîcheur
    {
        id: 'citron-gingembre',
        name: 'Citron Gingembre',
        description: 'Citron, Gingembre, Siank',
        price: 1500,
        category: 'fraicheur',
        image_url: 'https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?w=400'
    },
    {
        id: 'ananas-menthe',
        name: 'Ananas Menthe',
        description: "Jus d'Ananas, Menthe",
        price: 1500,
        category: 'fraicheur',
        image_url: 'https://images.unsplash.com/photo-1546171753-97d7676e4602?w=400'
    },
    // Classiques
    {
        id: 'sunrise-orange',
        name: 'Sunrise Orange',
        description: "Jus d'Orange, Grenadine",
        price: 1500,
        category: 'classiques',
        image_url: 'https://images.unsplash.com/photo-1560508179-b2c9a3f8e92b?w=400'
    },
    {
        id: 'red-power',
        name: 'Red Power',
        description: 'Bissap, Grenadine',
        price: 1500,
        category: 'classiques',
        image_url: 'https://images.unsplash.com/photo-1582106245687-cbb466a9f07f?w=400'
    },
    {
        id: 'cocktail-maison',
        name: 'Cocktail Maison',
        description: 'Jus Multifruit, Grenadine',
        price: 1200,
        category: 'classiques',
        image_url: 'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400'
    }
];

// Carte de cocktail
const CocktailCard = ({ cocktail, index }) => {
    const [imageError, setImageError] = useState(false);
    
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
            style={{ boxShadow: '0 10px 40px rgba(26, 61, 44, 0.1)' }}
        >
            {/* Image du cocktail */}
            <div className="relative aspect-square overflow-hidden">
                {cocktail.image_url && !imageError ? (
                    <img
                        src={cocktail.image_url}
                        alt={cocktail.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ 
                            background: `linear-gradient(135deg, ${BRAND_GREEN}10 0%, ${BRAND_GREEN}20 100%)` 
                        }}
                    >
                        <Wine className="w-16 h-16" style={{ color: `${BRAND_GREEN}30` }} />
                    </div>
                )}
                
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
            
            {/* Contenu */}
            <div className="p-5 text-center">
                {/* Nom en style cursif/élégant */}
                <h3 
                    className="text-xl font-serif italic mb-2"
                    style={{ color: BRAND_GREEN }}
                >
                    {cocktail.name}
                </h3>
                
                {/* Description */}
                <p className="text-gray-500 text-sm mb-3">
                    {cocktail.description}
                </p>
                
                {/* Prix */}
                <div 
                    className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold"
                    style={{ 
                        backgroundColor: `${BRAND_GREEN}10`,
                        color: BRAND_GREEN 
                    }}
                >
                    {cocktail.price?.toLocaleString('fr-FR')} F
                </div>
            </div>
        </motion.div>
    );
};

// Section de catégorie
const CategorySection = ({ category, cocktails }) => {
    if (cocktails.length === 0) return null;
    
    return (
        <section className="mb-16">
            {/* Titre de catégorie - Style élégant */}
            <div className="text-center mb-10">
                <div className="flex items-center justify-center gap-4 mb-2">
                    <div className="h-[1px] w-16" style={{ backgroundColor: GOLD }} />
                    <Sparkles className="w-5 h-5" style={{ color: GOLD }} />
                    <div className="h-[1px] w-16" style={{ backgroundColor: GOLD }} />
                </div>
                <h2 
                    className="text-2xl sm:text-3xl font-light tracking-[0.2em] uppercase mb-2"
                    style={{ color: BRAND_GREEN }}
                >
                    {category.name}
                </h2>
                <p className="text-gray-400 text-sm tracking-wide">
                    {category.description}
                </p>
            </div>
            
            {/* Grille de cocktails */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {cocktails.map((cocktail, index) => (
                    <CocktailCard 
                        key={cocktail.id} 
                        cocktail={cocktail} 
                        index={index}
                    />
                ))}
            </div>
        </section>
    );
};

// Page principale
const ClientCocktailsPage = () => {
    const [cocktails, setCocktails] = useState(DEFAULT_COCKTAILS);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchCocktails = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${API_URL}/api/client/cocktails`);
                if (response.data && response.data.length > 0) {
                    setCocktails(response.data);
                }
            } catch (error) {
                console.log('Using default cocktails data');
                // Keep default data on error
            } finally {
                setLoading(false);
            }
        };
        fetchCocktails();
    }, []);
    
    // Grouper les cocktails par catégorie
    const getCocktailsByCategory = (categoryId) => {
        return cocktails.filter(c => c.category === categoryId);
    };
    
    return (
        <div 
            className="min-h-screen"
            style={{ 
                background: 'linear-gradient(180deg, #fdfcfa 0%, #f9f7f4 100%)' 
            }}
        >
            {/* Header élégant */}
            <header className="relative py-10 sm:py-16 text-center overflow-hidden">
                {/* Motif décoratif subtil */}
                <div 
                    className="absolute inset-0 opacity-5"
                    style={{
                        backgroundImage: `radial-gradient(circle at 20% 50%, ${BRAND_GREEN} 1px, transparent 1px)`,
                        backgroundSize: '40px 40px'
                    }}
                />
                
                <div className="relative z-10 max-w-4xl mx-auto px-4">
                    {/* Bouton retour */}
                    <div className="flex justify-start mb-6">
                        <Link 
                            to="/client" 
                            className="flex items-center gap-1.5 text-sm transition-colors"
                            style={{ color: `${BRAND_GREEN}80` }}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Retour
                        </Link>
                    </div>
                    
                    {/* Logo */}
                    <Link to="/client" className="inline-block mb-8">
                        <img 
                            src={LOGO_URL} 
                            alt="Aldrin Restaurant" 
                            className="w-[200px] sm:w-[260px] mx-auto"
                        />
                    </Link>
                    
                    {/* Titre principal */}
                    <div className="mb-4">
                        <h1 
                            className="text-3xl sm:text-4xl lg:text-5xl font-serif italic mb-2"
                            style={{ color: BRAND_GREEN }}
                        >
                            Cocktails Sans Alcool
                        </h1>
                        <div className="flex items-center justify-center gap-3 text-sm tracking-[0.3em] uppercase text-gray-500">
                            <span>Rafraîchissants</span>
                            <span style={{ color: GOLD }}>•</span>
                            <span>Naturels</span>
                            <span style={{ color: GOLD }}>•</span>
                            <span>Élégants</span>
                        </div>
                    </div>
                    
                    {/* Ligne décorative */}
                    <div className="flex items-center justify-center gap-3 mt-6">
                        <div className="h-[1px] w-20" style={{ backgroundColor: GOLD }} />
                        <Wine className="w-6 h-6" style={{ color: GOLD }} />
                        <div className="h-[1px] w-20" style={{ backgroundColor: GOLD }} />
                    </div>
                </div>
            </header>
            
            {/* Contenu principal */}
            <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
                {loading ? (
                    // Skeleton loader
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {[...Array(7)].map((_, i) => (
                            <div 
                                key={i} 
                                className="bg-white rounded-3xl overflow-hidden animate-pulse"
                            >
                                <div className="aspect-square bg-gray-100" />
                                <div className="p-5 text-center space-y-3">
                                    <div className="h-6 bg-gray-100 rounded-lg w-3/4 mx-auto" />
                                    <div className="h-4 bg-gray-100 rounded w-1/2 mx-auto" />
                                    <div className="h-8 bg-gray-100 rounded-full w-1/3 mx-auto" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        {COCKTAIL_CATEGORIES.map(category => (
                            <CategorySection
                                key={category.id}
                                category={category}
                                cocktails={getCocktailsByCategory(category.id)}
                            />
                        ))}
                    </>
                )}
            </main>
            
            {/* Footer */}
            <footer className="py-10 text-center border-t border-gray-100 bg-white/80">
                <p 
                    className="text-sm tracking-wide"
                    style={{ color: `${BRAND_GREEN}60` }}
                >
                    Tous nos cocktails sont préparés avec des produits frais
                </p>
            </footer>
        </div>
    );
};

export default ClientCocktailsPage;
